import { Dispatch, SetStateAction, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useLocation } from 'react-router-dom';
import { type SidebarUserInfo } from 'types/user';
import LoginButtonArea from './LoginButtonArea';
import SidebarElement from './SidebarElement';
import UserInfo from './UserInfo';

import logoImg from 'assets/iopsspace.png';
import LanguageToggle from 'components/LanguageToggle';
import { useClickOutside } from 'hooks/useClickOutside';
import { useSelector } from 'react-redux';
import { RootState } from 'store';
import { SidebarSubMenu } from 'types/sidebar';
import { cn } from 'utils';

interface Props {
  isSidebarOpen: boolean;
  setIsSidebarOpen: Dispatch<SetStateAction<boolean>>;
}

const Sidebar = ({ isSidebarOpen, setIsSidebarOpen }: Props) => {
  const { pathname } = useLocation();
  const { ref } = useClickOutside(onClickOutside);

  function onClickOutside() {
    setIsSidebarOpen(false);
  }
  const userInfo: SidebarUserInfo | null = useSelector((state: RootState) => state.loginSlice.userInfo);
  const menu: SidebarSubMenu[] = useSelector((state: RootState) => state.loginSlice.menu);
  const { t } = useTranslation();

  // * localStorage에서 메뉴 권한 정보(MenuVisibility) 읽기
  const menuVisibility = useMemo(() => {
    try {
      const saved = localStorage.getItem('menuVisibility');
      return saved ? JSON.parse(saved) : {};
    } catch (error) {
      console.error('메뉴 권한 정보 읽기 실패:', error);
      return {};
    }
  }, []);

  // * 사용자 권한 체크 
  const userRole = userInfo?.roles?.includes('ROLE_ADMIN') ? 'admin' : 'user';

  return (
    <nav
      className={cn(
        'absolute z-20 m-index h-full w-60 min-w-60 max-w-60 overflow-x-auto bg-bg-layer2 py-2.5 pl-[18px] pr-3.5 shadow-sidebar transition-transform',
        { 'translate-x-0 shadow-sidebar': isSidebarOpen, 'translate-x-[-240px] shadow-none': !isSidebarOpen },
        'md:static md:translate-x-0 md:bg-bg-layer1 md:py-0 md:pl-2 md:pr-1 md:shadow-none'
      )}
      ref={ref}
    > 
      <div className="mb-2 py-[9px] pl-2">
        <Link to={'/'}>
          <img src={logoImg} className="h-[26px] w-[180px]" alt="iops-logo" />
        </Link>
      </div>
      <LanguageToggle />
      <ul className="flex h-fit w-full flex-col gap-5 py-3">
        {userInfo ? <UserInfo userInfo={userInfo} /> : <LoginButtonArea />}
        {menu &&

        // * Menu Visibility 메뉴 가시화 추가 (250807)
          menu.map((menuGroup) => {
            const visibleChildren = menuGroup.children.filter((subMenu) => {
              const menuPermission = menuVisibility[subMenu.path];
              // * 권한 정보가 없으면 Default 표시
              if (!menuPermission) return true;
              // * 해당 역할의 권한 확인
              return menuPermission[userRole] !== false;
            });

            // * 허용된 하위메뉴가 없으면 메뉴 그룹 자체를 숨김
            if (visibleChildren.length === 0) return null;

            return (
              <li key={menuGroup.subMenuTitle}>
                <div className="h-8 w-full px-2">
                  <span className="text-body2 font-regular text-text-tertiary">{t(menuGroup.subMenuTitle)}</span>
                </div>
                <ul className="flex w-full flex-col gap-1">
                  {visibleChildren.map((subMenu) => (
                    <SidebarElement key={subMenu.title} {...subMenu} selected={subMenu.path === pathname} />
                  ))}
                </ul>
              </li>
            );
          })}
        {/* {jwt && jwt['roles'].includes('ROLE_ADMIN') ? 
        sideBarAdminMenuList.map((menu) => {
          return (
            <li key={menu.subMenuTitle}>
              <div className="h-8 w-full px-2">
                <span className="text-body2 font-regular text-text-tertiary">{menu.subMenuTitle}</span>
              </div>
              <ul className="flex w-full flex-col gap-1">
                {menu.children.map((subMenu) => (
                  <SidebarElement key={subMenu.title} {...subMenu} selected={subMenu.path === pathname} />
                ))}
              </ul>
            </li>
          );
        }) :
        sideBarMenuList.map((menu) => {
          return (
            <li key={menu.subMenuTitle}>
              <div className="h-8 w-full px-2">
                <span className="text-body2 font-regular text-text-tertiary">{menu.subMenuTitle}</span>
              </div>
              <ul className="flex w-full flex-col gap-1">
                {menu.children.map((subMenu) => (
                  <SidebarElement key={subMenu.title} {...subMenu} selected={subMenu.path === pathname} />
                ))}
              </ul>
            </li>
          );
        })
      } */}
      </ul>
    </nav>
  );
};

export default Sidebar;
